import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { Stethoscope, MapPin, Award, ArrowRight, Loader2, Search, Mail, Check, UserPlus, Newspaper, Building2, Sparkles, Calendar, HelpCircle } from 'lucide-react';
import DoctorAvatar from '@/components/DoctorAvatar';
import JumpToNav from '@/components/JumpToNav';
import { buildSpecialtyFaqs, buildCitySpecialtyIntro, DOCSITE_AI_TEAM, TOP_CITIES } from '@/data/specialtyEnrichment';
import { LEGACY_SPECIALTY_REDIRECTS } from '@/data/platformSeoData';

function WaitlistCapture({ specialty, city, specialtyName }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setErr('');
    try {
      await api.post('/platform-seo/waitlist', { email, specialty, city });
      setSent(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save — try again');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-center gap-3" data-testid="waitlist-success">
        <Check size={20} className="text-emerald-600" />
        <p className="text-sm text-emerald-800">You're on the list. We'll notify you when a {specialtyName.toLowerCase()} signs up in {city || 'your area'}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-md mx-auto mt-6 rounded-2xl border border-sky-200 bg-sky-50/40 p-5" data-testid="waitlist-form">
      <p className="text-sm font-medium text-slate-900 mb-1">Are you a {specialtyName.toLowerCase()} in {city || 'this area'}?</p>
      <p className="text-xs text-slate-600 mb-3">Be the first listed here. Free for life + 3 months of TurboCharge on signup.</p>
      <div className="flex gap-2">
        <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-sky-400"
          data-testid="waitlist-email" />
        <button type="submit" disabled={busy}
          className="px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          data-testid="waitlist-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={14} /> Notify me</>}
        </button>
      </div>
      {err && <p className="text-xs text-rose-600 mt-2" data-testid="waitlist-error">{err}</p>}
    </form>
  );
}

export default function FindDoctorsPage() {
  const { specialty, city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Legacy slug? Redirect to canonical. Computed BEFORE useEffect so the effect
  // doesn't fire a network call on a slug we're about to leave.
  const redirectTo = LEGACY_SPECIALTY_REDIRECTS[specialty];

  useEffect(() => {
    if (redirectTo) return;          // skip — we're about to navigate away
    setLoading(true);
    const url = city ? `/find/${specialty}/${city}` : `/find/${specialty}`;
    api.get(url)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Not found'))
      .finally(() => setLoading(false));
  }, [specialty, city, redirectTo]);

  if (redirectTo) {
    return <Navigate to={city ? `/find/${redirectTo}/${city}` : `/find/${redirectTo}`} replace />;
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 size={24} className="animate-spin text-sky-600" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{error}</div>;
  if (!data) return null;

  const isEmpty = data.total === 0;
  const canonicalBase = 'https://docsite.care';
  const canonical = city
    ? `${canonicalBase}/find/${specialty}/${data.city_slug || city}`
    : `${canonicalBase}/find/${specialty}`;

  const title = city
    ? `${data.specialty_name} Doctors in ${data.city} — DocSite`
    : `${data.specialty_name} Doctors — DocSite`;
  const description = city
    ? `Find and book appointments with ${data.specialty_name} doctors in ${data.city}. ${data.total} doctors available on DocSite.`
    : `Find and book appointments with ${data.specialty_name} specialists across India. ${data.total} doctors on DocSite.`;

  const lastReviewed = new Date().toISOString().slice(0, 10);
  const cityIntro = buildCitySpecialtyIntro(data.specialty_name, data.city || 'India', data.total);
  const cityFaqs = buildSpecialtyFaqs(data.specialty_name, !!city, data.city || '');
  const otherCities = TOP_CITIES.filter(c => !city || c.slug !== city).slice(0, 8);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      description,
      url: canonical,
      publisher: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care' },
      author: {
        '@type': 'Organization',
        name: DOCSITE_AI_TEAM.name,
        url: DOCSITE_AI_TEAM.url,
        description: DOCSITE_AI_TEAM.description,
      },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: data.total,
        itemListElement: data.doctors.map((d, i) => ({
          '@type': 'ListItem', position: i + 1,
          item: { '@type': 'Physician', name: d.name, url: `${canonicalBase}/dr/${d.slug}` },
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: cityFaqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {isEmpty && <meta name="robots" content="noindex, follow" />}
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 via-white to-emerald-50 border-b border-slate-100">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3 flex items-center gap-1.5">
            <Stethoscope size={12} /> {data.specialty_name}
          </p>
          <h1 className="font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.25rem, 3.6vw, 4.25rem)', lineHeight: 1.1 }} data-testid="find-page-h1">
            {data.specialty_name} Doctors{city ? ` in ${data.city}` : ''}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-500" data-testid="find-byline">
            <span className="inline-flex items-center gap-1.5"><Sparkles size={11} className="text-sky-500" /> By <strong className="text-slate-700">DocSite AI Team</strong></span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> Last reviewed {lastReviewed}</span>
            <span className="text-slate-300">·</span>
            <span>{data.total} verified specialist{data.total !== 1 ? 's' : ''}</span>
          </div>
          <p className="text-slate-600 mt-6 leading-relaxed lg:max-w-[80ch]" style={{ fontSize: 'clamp(1.05rem, 1.15vw, 1.3rem)' }}>
            {cityIntro}
          </p>

          {/* City filter chips (only on specialty page without city) */}
          {!city && data.cities && data.cities.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="text-xs text-slate-500 py-1.5">Filter by city:</span>
              {data.cities.map(c => (
                <Link key={c} to={`/find/${specialty}/${c.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:bg-sky-50 hover:border-sky-200 hover:text-sky-700 transition-colors"
                  data-testid={`city-chip-${c}`}>
                  <MapPin size={10} className="inline mr-1" />{c}
                </Link>
              ))}
            </div>
          )}
          {city && (
            <Link to={`/find/${specialty}`} className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 mt-4 font-medium">
              View all {data.specialty_name} doctors across India
            </Link>
          )}

          <JumpToNav
            testId="find-jump-nav"
            items={[
              { icon: Search, label: 'Browse doctors', href: '#doctors-list', color: 'sky' },
              { icon: Newspaper, label: 'Medical insights', to: '/insights', color: 'emerald' },
              { icon: Building2, label: 'Other specialties', to: '/for', color: 'rose' },
              { icon: UserPlus, label: 'List my practice', to: `/register?as=doctor&from=find_${specialty}`, color: 'violet' },
            ]}
          />
        </div>
      </section>

      {/* Doctor Cards */}
      <section id="doctors-list" className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10 scroll-mt-24">
        {data.doctors.length === 0 ? (
          <div className="text-center py-20" data-testid="no-doctors-found">
            <Search size={40} className="text-slate-300 mx-auto mb-4" />
            <p className="text-lg text-slate-600">No {data.specialty_name.toLowerCase()} doctors found{city ? ` in ${data.city}` : ''} yet.</p>
            <p className="text-sm text-slate-500 mt-2">New doctors join DocSite every day. Check back soon!</p>
            <WaitlistCapture specialty={specialty} city={city ? data.city : ''} specialtyName={data.specialty_name} />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="doctor-cards-grid">
            {data.doctors.map(doc => (
              <Link key={doc.slug} to={`/dr/${doc.slug}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-200 hover:shadow-md transition-all group"
                data-testid={`doctor-card-${doc.slug}`}>
                <div className="flex items-start gap-4">
                  {/* Gendered silhouette fallback when photo_url is missing or
                      the stored URL fails to load. <DoctorAvatar/> handles both. */}
                  <DoctorAvatar
                    photoUrl={doc.photo_url}
                    gender={doc.gender}
                    name={doc.name}
                    size={64}
                    className="border border-slate-200 rounded-xl"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-sky-700 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {doc.name}
                    </h3>
                    {doc.qualifications && <p className="text-xs text-sky-600 mt-0.5">{doc.qualifications}</p>}
                    {doc.tagline && <p className="text-xs text-slate-500 mt-1 line-clamp-1">{doc.tagline}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4 text-xs text-slate-500">
                  {doc.experience_years > 0 && (
                    <span className="flex items-center gap-1"><Award size={12} className="text-amber-500" /> {doc.experience_years}+ yrs</span>
                  )}
                  {doc.city && (
                    <span className="flex items-center gap-1"><MapPin size={12} className="text-sky-500" /> {doc.city}</span>
                  )}
                  {doc.procedure_count > 0 && (
                    <span className="flex items-center gap-1"><Stethoscope size={12} className="text-emerald-500" /> {doc.procedure_count} procedures</span>
                  )}
                </div>
                {doc.consultation_fee && (
                  <p className="text-xs text-slate-600 mt-3">Consultation: <span className="font-semibold text-slate-900">Rs. {doc.consultation_fee}</span></p>
                )}
                <div className="mt-4 flex items-center gap-1 text-xs font-medium text-sky-600 group-hover:text-sky-700">
                  View Profile & Book <ArrowRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* FAQ Section — same FAQs as specialty page, but city-aware */}
      <section id="city-faqs" className="bg-slate-50/60 border-t border-slate-100 py-12 md:py-16 scroll-mt-24" data-testid="find-faqs">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-20">
          <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-2 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <HelpCircle size={20} className="text-sky-600" /> {data.specialty_name}{city ? ` in ${data.city}` : ''} — FAQ
          </h2>
          <p className="text-xs text-slate-500 mb-6">Generated and maintained by DocSite AI Team. Reviewed quarterly.</p>
          <div className="divide-y divide-slate-200 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {cityFaqs.map((f, i) => (
              <details key={i} className="group" data-testid={`find-faq-${i}`}>
                <summary className="flex items-start gap-3 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                  <span className="text-[10px] font-mono text-sky-500 mt-1.5 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <span className="flex-1 text-sm font-semibold text-slate-900 group-open:text-sky-700 transition-colors">{f.q}</span>
                  <ArrowRight size={14} className="text-slate-300 mt-1 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-5 pb-5 pl-12 text-sm text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other-cities cross-link section for internal linking + topical authority */}
      <section className="border-t border-slate-100 py-12 md:py-16" aria-label={`${data.specialty_name} doctors in other cities`} data-testid="find-other-cities">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <h2 className="text-base font-bold text-slate-500 uppercase tracking-[0.18em] mb-6">Browse {data.specialty_name.toLowerCase()} specialists in other cities</h2>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
            {otherCities.map(c => (
              <li key={c.slug}>
                <Link to={`/find/${specialty}/${c.slug}`} className="text-sky-700 hover:underline">
                  {data.specialty_name} in {c.name}
                </Link>
              </li>
            ))}
            <li className="col-span-2 md:col-span-4 mt-2">
              <Link to={`/find/${specialty}`} className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">All cities <ArrowRight size={12} /></Link>
            </li>
          </ul>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-400">
        <p>{data.specialty_name} Doctors on <Link to="/" className="text-sky-600 hover:underline">DocSite</Link> — Book appointments directly with self-declared medical practitioners.</p>
      </footer>
    </div>
  );
}
